/**
 * 再生中の音の実スペクトル取得（Web Audio AnalyserNode）。
 *
 * peaks は波形の振幅しか持たないため、生成サムネイルの「周波数帯域に応じた
 * 動き」は時定数違いのローパスで擬似的に作っていた。再生中のトラックに限れば
 * AnalyserNode で本物の帯域が取れるので、そのときはこちらを使う。
 *
 * ■ CORS の扱い（重要）
 * MediaElementAudioSourceNode は、元リソースが CORS 非対応のクロスオリジンだと
 * 仕様上「無音を出力する」。つまり解析データが取れないだけでなく音が消える。
 * さらに crossOrigin 属性は src の読み込み前に付いている必要があり、CORS 未設定の
 * S3 に対して付けると読み込み自体が失敗する。
 *
 * そのためこのモジュールは「CORS が使えると判ってから接続する」方針を取る。
 * サムネイルが並んだ時点で warmUpMediaCors() がオリジン単位に一度だけ実測し、
 * 再生が始まる頃には結果が出ている。判定が付いていなければ何もせず、
 * peaks 由来の駆動にそのままフォールバックする（音への副作用ゼロ）。
 */

import { createFeatureExtractor, SpectrumFrame } from './spectrumFeatures';

/** 解析に使う FFT サイズ。48kHz で 1 ビン ≒ 23Hz */
const FFT_SIZE = 2048;
/** 同一フレームとみなす時間（ms）。120fps でも 1 回に丸まる */
const FRAME_CACHE_MS = 7;

export interface SpectrumSource {
  /** 現フレームの特徴量。まだ音が来ていなければ null */
  read(): SpectrumFrame | null;
  /** 解析タップを外す（音の経路には影響しない） */
  release(): void;
}

// =============================================================================
// CORS の実測
// =============================================================================

/** オリジン → CORS が使えるか。判定中は Promise を入れて重複実行を防ぐ */
const corsByOrigin = new Map<string, boolean | Promise<boolean>>();

function originOf(url: string): string | null {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
}

/**
 * オリジン単位で CORS の可否を実測し、結果をキャッシュする。
 * 1 バイトだけの Range リクエストなので転送量はほぼゼロ。
 * サムネイルの表示時に呼んでおけば、再生が始まる頃には判定が済んでいる。
 */
export function warmUpMediaCors(url: string | null | undefined): void {
  if (!url) return;
  const origin = originOf(url);
  if (!origin || origin === window.location.origin) return;
  if (corsByOrigin.has(origin)) return;

  const probe = fetch(url, { method: 'GET', headers: { Range: 'bytes=0-1' }, mode: 'cors' })
    .then(res => {
      // 本文は要らないので即座に破棄する
      res.body?.cancel().catch(() => {});
      return res.ok || res.status === 206;
    })
    .catch(() => false)
    .then(ok => {
      corsByOrigin.set(origin, ok);
      return ok;
    });

  corsByOrigin.set(origin, probe);
}

/**
 * このオリジンでは CORS を使わないと記録する。
 *
 * 実測が通っても、同じ URL が先に CORS なしで取得されているとブラウザの
 * キャッシュエントリが非 CORS 扱いのまま残り、あとから crossOrigin 付きで
 * 読もうとすると失敗することがある。実際に読み込みが落ちたときの逃げ道。
 */
export function markMediaCorsUnusable(url: string | null | undefined): void {
  if (!url) return;
  const origin = originOf(url);
  if (origin) corsByOrigin.set(origin, false);
}

/**
 * CORS が使えると判っているか（同期）。
 * 判定前・判定失敗はいずれも false。「判らないなら触らない」を徹底する。
 */
export function isMediaCorsUsable(url: string | null | undefined): boolean {
  if (!url) return false;
  const origin = originOf(url);
  if (!origin) return false;
  // 同一オリジンは CORS の制約を受けない
  if (origin === window.location.origin) return true;
  return corsByOrigin.get(origin) === true;
}

// =============================================================================
// 解析タップ
// =============================================================================

let sharedContext: AudioContext | null = null;
/** 要素ごとの MediaElementAudioSourceNode。同じ要素に 2 回作ると例外になる */
const sourceByElement = new WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>();

function getSharedContext(): AudioContext | null {
  if (sharedContext) return sharedContext;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  sharedContext = new Ctor();
  return sharedContext;
}

/**
 * サスペンド状態を解除する。
 * 一度でも要素を AudioContext に繋ぐと音はグラフ経由になるため、suspended の
 * ままだと解析どころか音そのものが出ない。再生開始のたびに呼ぶこと。
 */
export function resumeAudioContext(): void {
  if (sharedContext?.state === 'suspended') sharedContext.resume().catch(() => {});
}

/**
 * 再生中の要素に解析タップを付ける。
 *
 * 音の経路（source → destination）とは別に analyser へ分岐させる。こうすると
 * 解析を外すときに analyser を切るだけで済み、音の経路に触らずに済む。
 *
 * CORS が確認できていない場合は接続しない（接続すると無音になるため）。
 */
export function attachSpectrum(element: HTMLMediaElement): SpectrumSource | null {
  if (!isMediaCorsUsable(element.currentSrc || element.src)) return null;

  const context = getSharedContext();
  if (!context) return null;

  let analyser: AnalyserNode;
  try {
    let source = sourceByElement.get(element);
    if (!source) {
      source = context.createMediaElementSource(element);
      source.connect(context.destination);
      sourceByElement.set(element, source);
    }
    analyser = context.createAnalyser();
    analyser.fftSize = FFT_SIZE;
    analyser.smoothingTimeConstant = 0.5;
    source.connect(analyser);
  } catch (error) {
    console.warn('[audioAnalyser] 解析タップの接続に失敗:', error);
    return null;
  }

  resumeAudioContext();

  const binCount = analyser.frequencyBinCount;
  const db = new Float32Array(binCount);
  const magnitude = new Float32Array(binCount);
  const extract = createFeatureExtractor(context.sampleRate, binCount);

  let lastReadMs = performance.now();
  // 一度も音が来ていない間は null を返す。呼び出し側はこれを見て peaks 由来の
  // 駆動に留まる（再生前から「無音」と答えると動きが止まってしまう）
  let hasSignal = false;
  // 同一フレーム内の再計算を防ぐキャッシュ。read() は分類器と複数のサムネイルから
  // 呼ばれるが、flux は前フレームとの差分なので 2 回目以降は必ず 0 になってしまう
  let cachedFrame: SpectrumFrame | null = null;
  let cachedAtMs = -1;

  return {
    read(): SpectrumFrame | null {
      const readMs = performance.now();
      if (readMs - cachedAtMs < FRAME_CACHE_MS) return cachedFrame;
      cachedAtMs = readMs;

      analyser.getFloatFrequencyData(db);
      for (let i = 0; i < binCount; i++) {
        // dB → 線形振幅。-Infinity（無音ビン）は 0 に落ちる
        magnitude[i] = db[i] <= -140 ? 0 : Math.pow(10, db[i] / 20);
      }

      const dtSec = (readMs - lastReadMs) / 1000;
      lastReadMs = readMs;

      const frame = extract(magnitude, dtSec);
      if (frame.level > 0) hasSignal = true;
      cachedFrame = hasSignal ? frame : null;
      return cachedFrame;
    },
    release(): void {
      try {
        analyser.disconnect();
      } catch {
        // 破棄済みグラフの disconnect 失敗は無視
      }
    },
  };
}
